import ng from 'angular';
import {
    AccountActivation,
    AccountActivationRequests,
    BankAccount,
    APISingleResponse,
    BankAccountCreateData,
} from './account-activation';
import { PaymentMethod, } from './components/payment-options/payment-options';
import { AlertManagerService, ApiError, NavigationService, RpcClient, RpcClientService, SentryErrorEmitterService } from '@/services';
import { BillingApi, UI } from '@/types';
import { SupportFormData } from '@/atomic-components';
import { getEnvironmentData } from 'worker_threads';
import { Severity } from '@sentry/browser';

export const BillingClientService = [
    'rpcClient',
    (platformRpcClient: RpcClientService) => platformRpcClient.Factory('/api/billing/v1/json/', 'billing')
];

export type PaymentAction = 'cancel' | 'commit';
type PageNames = 'basicData' | 'paymentOptions' | 'finished' | 'paymentMigrated'

export class TemplateAccountActivationController {
    public static $inject: string[] = [
        '$scope',
        '$translate',
        'billingClient',
        'accountClient',
        'alertManager',
    ];

    public logoNetim = '/assets/images/logos/netim-transfer.svg';
    public logoHosting = '/assets/images/logos/hosting-transfer.svg';

    public loading = true;
    public callbackLoading = false;

    public action: PaymentAction;
    public token: string;
    public selectedPaymentMethod: PaymentMethod;
    public name: string;
    public email: string;
    public street: string;
    public zipCode: string;
    public city: string;
    public country: string;
    public finishCheckboxChecked = false;
    public page: PageNames = 'basicData';
    public paymentId: string;
    public password = '';
    public passwordCreated = false;
    public paymentMethod: string;
    public paymentMethodMigrated = false;
    public paymentMethodCreated = false;
    public phone: string;
    public prepaidAccepted = false;
    public status: string;
    public success = false;
    public sepaData: {
        accountHolder: string,
        iban: string,
        bic: string,
    };
    public supportFormData: SupportFormData = {
        accountId: 'string',
        accountName: 'string',
        data: 'any',
        location: 'string',
        mail: 'string',
        name: 'string',
    };
    public sepaDataIsValid = false;

    public basicDataFormIsValid = false;
    private accountActivation: AccountActivation;

    constructor(
        private $scope: ng.IScope,
        private $translate: ng.translate.ITranslateService,
        private billingClient: RpcClient,
        private accountClient: RpcClient,
        private alertManager: AlertManagerService,
    ) {}

    public async $onInit(): Promise<void> {
        SentryErrorEmitterService.setTags({ AccountActivation: 'init' });
        const requests: AccountActivationRequests = {
            loadAccountActivationDetails: (token: string): Promise<UI.APISingleResponse<BillingApi.AccountActivationDetails>> => {
                const request = this.billingClient.request('accountActivationDetails');
                request.unauthenticated();
                return request.execute({ activationToken: token });
            },
            saveAccountActivationBasicData: (parameters: {[key: string]: string}): Promise<void> => {
                const request = this.accountClient.request('accountActivationSetBasicData');
                request.unauthenticated();
                return request.execute(parameters);
            },
            accountActivationPaymentAccountCreate: (parameters: BillingApi.PaymentAccountCreateParameters): Promise<UI.APISingleResponse<BillingApi.StripePaymentStartResult>> => {
                const request = this.billingClient.request('accountActivationPaymentAccountCreate');
                request.unauthenticated();
                return request.execute({ activationToken: this.token, ...parameters });
            },
            accountActivationPaymentAccountCommit: (paymentAccountId: string): Promise<void> => {
                const request = this.billingClient.request('accountActivationPaymentAccountCommit');
                request.unauthenticated();
                return request.execute({ activationToken: this.token, paymentAccountId });
            },
            accountActivationPaymentAccountCancel: (paymentAccountId: string): Promise<void> => {
                const request = this.billingClient.request('accountActivationPaymentAccountCancel');
                request.unauthenticated();
                return request.execute({ activationToken: this.token, paymentAccountId });
            },
            createBankAccount: (
                bankAccountCreateData: BankAccountCreateData,
            ): Promise<APISingleResponse<BankAccount>> => {
                const request = this.billingClient.request('accountActivationBankAccountCreate');
                request.unauthenticated();
                return request.execute({ bankAccountCreateData, activationToken: this.token });
            },
            accountActivationSetAdvertisingOptIn: (advertisingOptIn: boolean): Promise<void> => {
                const request = this.accountClient.request('accountActivationSetAdvertisingOptIn');
                request.unauthenticated();
                return request.execute({ activationToken: this.token, advertisingOptIn });
            },
            accountActivationPaymentPrepaid: (): Promise<void> => {
                const request = this.billingClient.request('accountActivationPaymentPrepaid');
                request.unauthenticated();
                return request.execute({ activationToken: this.token });
            }
        }

        this.accountActivation = new AccountActivation(this.token, requests);
        await this._loadAccountData().catch(this.handleDataError);

        await this._getPaymentStatus(this.action, this.paymentId).catch(this.handleDataError);
        this.accountActivation.loadStripeScript();

        // giving stripe script chance to load
        setTimeout(() => {
            this.loading = false;
            this.$scope.$apply();
            SentryErrorEmitterService.setTags({ AccountActivation: this.page });
        }, 350);
    }

    /*
        shows checkmarks when already on the following site
    */
    public get showChecks(): boolean[] {
        return [
            ['paymentOptions', 'paymentMigrated', 'finished'].includes(this.page),
            ['finished', 'paymentMigrated'].includes(this.page),
            ['finished'].includes(this.page),
        ]
    }

    public get passwordIsSet(): boolean {
        if (this.passwordCreated) {
            return true;
        }
        return false;
    }

    public get disableBasicDataContinueButton(): boolean {
        return !this.basicDataFormIsValid || this.password.length === 0;
    }

    public get disablePaymentButton(): boolean {
        if (this.selectedPaymentMethod === 'postpaid') {
            return [undefined, ''].indexOf(this.sepaData?.accountHolder) >= 0
                || [undefined, ''].indexOf(this.sepaData?.iban) >= 0
                || [undefined, ''].indexOf(this.sepaData?.bic) >= 0
                || !this.sepaDataIsValid;
        }
        if (this.selectedPaymentMethod === 'prepaid') {
            return !this.prepaidAccepted;
        }
        return this.selectedPaymentMethod === undefined;
    }

    public get allDone(): boolean {
        return this.status === 'finished';
    }

    public get isButtonDisabled(): boolean {
        if (this.callbackLoading) return true;
        switch(this.page) {
            case 'paymentOptions':
                return this.disablePaymentButton;
            case 'basicData':
                return this.disableBasicDataContinueButton;
            case 'paymentMigrated':
            case 'finished':
                return false;
        }
    }

    public executeButtonCallback = (): void => {
        switch(this.page) {
            case 'basicData':
                this.basicDataCallback();
                break;
            case 'paymentOptions':
                this.paymentOptionsCallback();
                break;
            case 'paymentMigrated':
                this.paymentMigratedCallback();
                break;
            case 'finished':
                this.finishCallback();
                break;
        }
    }

    public get buttonText(): string {
        switch(this.page) {
            case 'basicData':
                return this.basicDataButtonTitle();
            case 'paymentOptions':
            case 'paymentMigrated':
                return this.paymentButtonTitle();
            case 'finished':
                return this.finishButtonTitle();
        }
    }

    public basicDataCallback = (): void => {
        const parameters = {
            activationToken: this.token,
            password: this.password,
            street: this.street,
            zipCode: this.zipCode,
            city: this.city,
            phone: this.phone
        }
        this.callbackLoading = true;
        this.accountActivation.saveAccountActivationBasicData(
            parameters
        ).then(() => {
            this.callbackLoading = false;
            if (this.paymentMethodMigrated) {
                this.goToSubPage('paymentMigrated');
                return;
            }
            this.goToSubPage('paymentOptions');
        }).catch(this.handleCallbackError)

        // TODO fix passwort is not set
    }

    public paymentMigratedCallback = (): void => {
        this.goToSubPage('finished');
    }

    public paymentOptionsCallback = (): void => {
        if (this.selectedPaymentMethod === 'postpaid') {
            this.callbackLoading = true;
            void this.accountActivation.createBankAccount({
                iban: this.sepaData.iban,
                accountHolder: this.sepaData.accountHolder,
                bic: this.sepaData.bic
            }).then(() => {
                setTimeout(() => {
                    this.paymentMethodCreated = true;
                    this.callbackLoading = false;
                    this.goToSubPage('finished');
                    this.$scope.$apply();
                })
            }).catch(this.handleCallbackError);
        } else if (this.selectedPaymentMethod === 'prepaid') {
            this.callbackLoading = true;
            void this.accountActivation.accountActivationPaymentPrepaid().then(
                () => {
                    setTimeout(() => {
                        this.paymentMethodCreated = true;
                        this.callbackLoading = false;
                        this.goToSubPage('finished');
                        this.$scope.$apply();
                    })
                }
            ).catch(this.handleCallbackError);
        } else {
            this.callbackLoading = true;
            void this.accountActivation.createPaymentAccount(this.selectedPaymentMethod).then(
                (result: UI.APISingleResponse<BillingApi.StripePaymentStartResult>): any => {
                    if (result.response.paymentPublicKey.length > 0) {
                        const stripe = Stripe(result.response.paymentPublicKey); // eslint-disable-line new-cap
                        return void stripe.redirectToCheckout({
                            sessionId: result.response.externalPaymentSession
                        }).catch(this.handleCallbackError);
                    };
                }
            ).catch(this.handleCallbackError);
        }
    }

    public getIgnoredErrors = () => {
        return {
            20005: /* translationId */ 'ERRORS.20005',
            62055: /* translationId */ 'TR_230119-81c8a2_TR',
            62056: /* translationId */ 'TR_230119-cfe94a_TR',
            62057: /* translationId */ 'TR_230119-a19bab_TR',
            62058: /* translationId */ 'TR_230119-58aa69_TR',
            62060: /* translationId */ 'TR_230119-aa192d_TR',
            62061: /* translationId */ 'TR_230119-1a2625_TR',
            62062: /* translationId */ 'TR_230119-d6f3a0_TR',
            62063: /* translationId */ 'TR_230119-922dc6_TR',
        }
    }

    public handleCallbackError = (err: string | Error) => {
        let ignoreSentry = false;

        if (err.hasOwnProperty('code') && this.getIgnoredErrors().hasOwnProperty((err as any).code)) {
            ignoreSentry = true;
        }

        let message;
        switch (true) {
            case typeof err === 'string':
                message = err;
                break;
            case [null, undefined].indexOf(err) >= 0:
                message = 'undefined error';
                break;
            case err.hasOwnProperty('message'):
                message = err.message;
                break;

            default:
                try {
                    message = err.toString();
                } catch {
                    message = this.$translate.instant('TR_030725-cea024_TR');
                }
                break;
        }

        if (!ignoreSentry) {
            SentryErrorEmitterService.sendSentryReport(err, null, { AccountActivation: 'callback' }, Severity.Error);
        }
        this.alertManager.error(message, null, null, null, true);

        setTimeout(() => {
            this.callbackLoading = false;
        })
    }

    public handleDataError = (err: string | Error) => {
        SentryErrorEmitterService.sendSentryReport(err, null, { AccountActivation: 'data' }, Severity.Error);
        const message = this.$translate.instant('TR_020725-083150_TR');
        this.alertManager.error(message, null, null, null, true);

        setTimeout(() => {
            this.loading = false;
        })
    }

    /*
        pure ui logic to give some slight delay to make it feel smoother
        - not used yet.
    */
    public uiTimer = async (ms: number): Promise<void> => {
        return new Promise(function(resolve) {setTimeout(resolve, ms)});
    }

    public finishCallback = async () => {
        this.callbackLoading = true;
        this.uiTimer(250).then(() => {
            setTimeout(() => {
                this.callbackLoading = false;
                this.$scope.$apply();
                window.location.href = 'login';
            });
        });
    }

    public newsletterToggleCallback = async (value: boolean): Promise<void> => {
        return await this.accountActivation.accountActivationSetAdvertisingOptIn(value).catch((err) => {
            this.handleCallbackError(err);
            return Promise.reject();
        });
    }

    private _loadAccountData = async () => {
        await this.accountActivation.loadAccountActivationDetails().then(() => {
                this.finishCheckboxChecked = this.accountActivation.userDetails.advertisingOptIn;

                this.name = this.accountActivation.userDetails.name;
                this.email = this.accountActivation.userDetails.email;
                this.street = this.accountActivation.userDetails.street;
                this.zipCode = this.accountActivation.userDetails.zipCode;
                this.city = this.accountActivation.userDetails.city;
                this.country = this.accountActivation.userDetails.country;
                this.phone = this.accountActivation.userDetails.phone;
                this.passwordCreated = this.accountActivation.userDetails.passwordCreated;
                this.paymentMethod = this.accountActivation.userDetails.paymentMethod;
                this.paymentMethodMigrated = this.accountActivation.userDetails.paymentMethodMigrated;
                this.paymentMethodCreated = this.accountActivation.userDetails.paymentMethodCreated;
                this.status = this.accountActivation.userDetails.status;
                this._setInitialPage();

                this.supportFormData.mail = this.accountActivation.userDetails.email;
                this.supportFormData.name = this.accountActivation.userDetails.name;

                SentryErrorEmitterService.setUser(this.email);
            }
        ).catch((err) => Promise.reject(err));

    }

    private async _getPaymentStatus(action: PaymentAction, paymentId: string) {
        const hasPaymentMethod = (
            this.paymentMethodMigrated ||
            this.paymentMethodCreated ||
            !this.passwordCreated ||
            this.selectedPaymentMethod === 'postpaid'
        );

        if (['cancel', 'commit'].indexOf(action) < 0 || hasPaymentMethod) {
            return Promise.resolve();
        }

        if (action === 'commit' && paymentId !== undefined) {
            await this.accountActivation.accountActivationPaymentAccountCommit(paymentId).then(() => {
                this.goToSubPage('finished');
            }).catch((err) => Promise.reject(err));
        }

        if (action === 'cancel' && paymentId !== undefined) {
            await void this.accountActivation.accountActivationPaymentAccountCancel(
                paymentId
            ).finally(() => {
                const message = this.$translate.instant('TR_020725-4c0713_TR');
                this.alertManager.warning(message);
                return Promise.resolve();
            });
        }
    }

    private _setInitialPage() {
        if (this.status === 'finished') {
            this.goToSubPage('finished');
            return;
        }
        if (this.passwordCreated) {
            this.goToSubPage('paymentOptions');
        }
    }

    public getTransferText = () => {
        return this.$translate.instant('TR_170625-8c54bc_TR', { companyFriendly: 'hosting.fr' });
    }

    public get headline(): {
        title: string,
        subtitle: string,
    } {
        switch(this.page) {
            case 'basicData':
                return {
                    title: this.$translate.instant('TR_160625-9d4a88_TR', { name: this.name }),
                    subtitle: this.$translate.instant('TR_160625-ada2e8_TR'),
                };
            case 'paymentMigrated':
                return {
                    title: this.$translate.instant('TR_160625-27db36_TR'),
                    subtitle: '',
                };
            case 'paymentOptions':
                return {
                    title: this.$translate.instant('TR_160625-27db36_TR'),
                    subtitle: this.$translate.instant('TR_160625-55c49d_TR'),
                };
            case 'finished':
                return {
                    title: this.$translate.instant('TR_160625-582017_TR'),
                    subtitle: this.$translate.instant('TR_160625-412cc4_TR'),
                };
        }
    }

    public basicDataButtonTitle = () => {
        return this.$translate.instant('GENERAL.WIZARD.NEXT');
    }

    public paymentButtonTitle = () => {
        if (this.page === 'paymentMigrated') return this.$translate.instant('GENERAL.WIZARD.NEXT');
        return this.$translate.instant('TR_180625-780f04_TR');
    }

    public finishButtonTitle = () => {
        return this.$translate.instant('TR_250625-97f986_TR');
    }

    public get isFinishPage(): boolean {
        return this.page === 'finished';
    }

    public get isPaymentPage(): boolean {
        return this.page === 'paymentOptions';
    }

    public isPrepaid(method: string): boolean {
        return method === 'prepaid';
    }

    public goToSubPage = (page: PageNames) => {
        SentryErrorEmitterService.setTags({ AccountActivation: page });
        this.page = page;
    }

}

export class TemplateAccountActivationComponent implements ng.IComponentOptions {
    public bindings = {
        token: '<',
        paymentId: '<?',
        action: '@?',
    };
    public template = require('./account-activation-template.html');
    public controller = TemplateAccountActivationController;
}
